/**
 * /signup — new user account creation.
 *
 * Posts to /api/auth/signup. On success: stores the returned JWT via
 * useAuth.loginWithJwt and redirects to /app/settings (Free tier
 * lands there since the heatmap is tier-gated to Essentials+ once
 * Day 4-5 lands — for now it'll still let them through).
 *
 * Design parity with /login: same chrome, same form treatment,
 * same shake-on-error pattern. Different verb ("CREATE ACCOUNT")
 * and the rate-limit-error string is more user-friendly since
 * signup hits 5/hr (legitimate-looking accidents are more likely
 * than login's 30/hr cap).
 */

import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Logo from '../../components/chrome/Logo';
import ChartGridBackground from '../../components/login/ChartGridBackground';
import AmbientStrip from '../../components/login/AmbientStrip';
import { apiPost } from '../../lib/api';
import { useAuth } from '../../hooks/useAuth';
import type { AuthResponse } from '../../lib/types';

type ErrorKind = null | 'invalid' | 'duplicate' | 'short-password' | 'rate-limited' | 'network';

function errorMessage(kind: ErrorKind): React.ReactNode {
  switch (kind) {
    case 'invalid':       return 'That email address looks invalid.';
    case 'duplicate':     return (
      <>
        Could not create account. If this email is already registered,{' '}
        <Link to="/login" style={{ color: 'var(--accent)', textDecoration: 'underline' }}>
          sign in instead
        </Link>.
      </>
    );
    case 'short-password': return 'Password must be at least 8 characters.';
    case 'rate-limited':   return 'Too many signups from this device. Try again in an hour.';
    case 'network':        return 'Connection lost. Check your internet and try again.';
    default:               return null;
  }
}

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { authed, loginWithJwt } = useAuth();
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // Prefill from landing hero form (?email=...) or fall back to empty.
  const prefillEmail = searchParams.get('email') ?? '';

  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<ErrorKind>(null);

  // Already logged in? Bounce straight to the app.
  useEffect(() => {
    if (authed) navigate('/app/settings', { replace: true });
  }, [authed, navigate]);

  useEffect(() => {
    // If the email is prefilled, focus the password field directly.
    if (prefillEmail) {
      passwordRef.current?.focus();
    } else {
      emailRef.current?.focus();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clear = (cb: (v: string) => void) =>
    (e: React.ChangeEvent<HTMLInputElement>) => { setError(null); cb(e.target.value); };

  const shakeCard = () => {
    const card = cardRef.current;
    if (!card) return;
    card.classList.remove('shake');
    void card.offsetWidth;
    card.classList.add('shake');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password || busy) return;
    if (password.length < 8) {
      setError('short-password'); shakeCard(); return;
    }
    setBusy(true);
    setError(null);
    try {
      const resp = await apiPost<AuthResponse>('/auth/signup', {
        email: email.trim().toLowerCase(),
        password,
      });
      loginWithJwt(resp.token);
      setSuccess(true);
      cardRef.current?.classList.add('login-card--success');
      setTimeout(() => navigate('/app/settings', { replace: true }), 700);
    } catch (err) {
      const ae = err as { status?: number };
      const status = ae?.status ?? 0;
      if (status === 409)        setError('duplicate');
      else if (status === 422)   setError('short-password');
      else if (status === 429)   setError('rate-limited');
      else if (status === 0)     setError('network');
      else                       setError('invalid');
      shakeCard();
      setBusy(false);
    }
  };

  const errMsg = errorMessage(error);

  return (
    <div className="login-page">
      <div className="login-bg-grid" aria-hidden="true" />
      <ChartGridBackground />
      <AmbientStrip />

      <div className="login-main">
        <div className="login-logo">
          <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            <Logo width={56} height={28} title="GammaVision" />
            <h1 className="login-title">GAMMAVISION</h1>
            <div className="login-sub">GEX Terminal · Free Account</div>
          </div>
        </div>

        <div className="login-card-wrap">
          <div ref={cardRef} className={`login-card${error ? ' login-card--err' : ''}`}>
            <div style={{ textAlign: 'center' }}>
              <span className="login-pill">
                <span className="dot" aria-hidden="true" /> CREATE FREE ACCOUNT
              </span>
            </div>
            <div className="login-head">
              <h2>Start with the basics</h2>
              <p>Free tier — manage your account + upgrade when ready.</p>
            </div>

            <form onSubmit={handleSubmit} aria-busy={busy} aria-label="Sign up">
              <div className="login-field">
                <label htmlFor="signup-email">EMAIL</label>
                <input
                  id="signup-email"
                  ref={emailRef}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="off"
                  spellCheck={false}
                  required
                  value={email}
                  onChange={clear(setEmail)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { e.preventDefault(); passwordRef.current?.focus(); }
                  }}
                  className={error ? 'has-error' : undefined}
                  aria-invalid={!!error}
                />
              </div>
              <div className="login-field">
                <label htmlFor="signup-password">PASSWORD <span style={{ color: 'var(--text-4)', fontWeight: 400 }}>(min 8)</span></label>
                <input
                  id="signup-password"
                  ref={passwordRef}
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={password}
                  onChange={clear(setPassword)}
                  className={error ? 'has-error' : undefined}
                  aria-invalid={!!error}
                />
              </div>

              {errMsg && <div role="alert" className="login-error">{errMsg}</div>}

              <button
                type="submit"
                className="login-submit"
                disabled={busy || !email || !password || success}
                aria-busy={busy}
              >
                {success ? (
                  <>ACCOUNT CREATED <span aria-hidden="true">✓</span></>
                ) : busy ? (
                  <>
                    <span className="login-spinner" aria-hidden="true" />
                    CREATING…
                  </>
                ) : (
                  <>CREATE ACCOUNT <span aria-hidden="true">→</span></>
                )}
              </button>
            </form>

            <p className="gv-funnel-trial-note">
              Free account today · your 7-day full-access trial starts at launch · we'll email you the moment we're live.
            </p>

            <p style={{
              marginTop: '0.75rem', textAlign: 'center',
              fontSize: '0.78rem', color: 'var(--text-3)',
            }}>
              Already have an account?{' '}
              <Link to="/login" style={{ color: 'var(--accent)', textDecoration: 'underline' }}>
                Sign in
              </Link>
            </p>
          </div>
        </div>

        <p style={{
          marginTop: '1.5rem', textAlign: 'center', fontSize: '0.7rem',
          color: 'var(--text-4)', letterSpacing: '0.04em',
        }}>
          By signing up you agree to our{' '}
          <Link to="/terms" style={{ color: 'inherit', textDecoration: 'underline' }}>terms of use</Link>{' '}
          and{' '}
          <Link to="/privacy" style={{ color: 'inherit', textDecoration: 'underline' }}>privacy policy</Link>.
          GammaVision is an analytical tool, not investment advice.
        </p>
      </div>
    </div>
  );
};

export default Signup;
